import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
// Import Libraries
import { Link } from 'gatsby';
// Import Icons
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
// Import configData
import configData from '../../../../configData.json';
// Import Hooks
import useTodayOpeningOverride from '../../../hooks/useTodayOpeningOverride';
import useOpeningTimesMessage from '../../../hooks/useOpeningTimesMessage';

const NavbarBannerStyling = styled.div`
	height: 37px;
	background-color: var(--white);
	border-bottom: 1px solid var(--neutral-10);
	padding: 8px 64px;
	display: flex;
	justify-content: end;
	align-items: center;
	ul {
		display: flex;
		align-items: center;
		gap: 32px;
		li {
			a {
				color: var(--neutral-75);
			}
			&.darwin {
				text-decoration: underline;
			}
			&.open {
				color: var(--red);
				position: relative;
				cursor: pointer;
			}
		}
	}

	.hours-dropdown {
		position: relative;
		.dropdown-header {
			display: flex;
			align-items: center;
			gap: 8px;
			cursor: pointer;
		}
		.dropdown-body {
			position: absolute;
			top: 100%;
			right: 0;
			margin-top: 8px;
			background: var(--white);
			border: 1px solid var(--neutral-10);
			box-shadow: var(--box-shadow-small);
			width: 500px;
			z-index: 100;
			display: none;
			&.open {
				display: block;
			}
			.dropdown-title {
				padding: 20px 16px 12px;
				color: var(--red);
			}
			.dropdown-item {
				padding: 8px 16px;
				color: var(--neutral-75);
				justify-content: space-between;
    			display: flex;
			}
		}
	}

	@media (max-width: 992px) {
		display: none;
	}
`;

const NavbarBanner = () => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const {hours} = useTodayOpeningOverride();
    const displayOpen = useOpeningTimesMessage({ hours });

	const formatTime = (timeString) => {
		if (!timeString || typeof timeString !== 'string') return '';
		const parts = timeString.split(':');
		const hours = parseInt(parts[0], 10);
		const minutes = parts[1] || '00';
		const hoursPadded = hours.toString().padStart(2, '0');
		const minutesPadded = minutes.padStart(2, '0');
		return `${hoursPadded}:${minutesPadded}`;
	};

	const formatDate = (dateString) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleDateString('en-GB', { 
			weekday: 'long',
			day: 'numeric', 
			month: 'long'
		});
	};

	const formatHours = (hourItem) => {
		if (!hourItem.startHour && !hourItem.endHour) {
			return 'Closed';
		}
		if (!hourItem.startHour || !hourItem.endHour) {
			return '';
		}
		return `${formatTime(hourItem.startHour)} - ${formatTime(hourItem.endHour)}`;
	};

	const hasHours = hours && hours.hours && Array.isArray(hours.hours) && hours.hours.length > 0;

	const toggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen);
	};

	return (
		<NavbarBannerStyling>
			<ul>
				{/* FOLLOW UP: uncomment once concierge login is ready for live */}
				{/* <li className='darwin'>
					<Link to={configData.enquiry.darwin.link}>
						<p>{configData.enquiry.darwin.text}</p>
					</Link>
				</li> */}
				<li>
					<Link id="brochure-request-nav" to={configData.enquiry.brochure.link}>
						<p>{configData.enquiry.brochure.text}</p>
					</Link>
				</li>
				<li className='open'>
					{hasHours ? (
						<div className='hours-dropdown'>
							<div 
								className='dropdown-header' 
								onClick={toggleDropdown}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										toggleDropdown();
									}
								}}
								role="button"
								tabIndex={0}
								aria-expanded={isDropdownOpen}
								aria-haspopup="true"
							>
								<span>{displayOpen}</span>
								{isDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
							</div>
							<div className={`dropdown-body ${isDropdownOpen ? 'open' : ''}`}>
								{hours.title && (
									<div className='dropdown-title'>{hours.title}</div>
								)}
								{hours.hours.map((hourItem, index) => (
									<div key={index} className='dropdown-item'>
										<span>{formatDate(hourItem.date)}</span> <span>{formatHours(hourItem)}</span>
									</div>
								))}
							</div>
						</div>
					) : (
						<span>{displayOpen}</span>
					)}
				</li>
				{/* Temp to show easter opening times */}
				{/* <li><Link to="/easter-opening-times">Easter Opening Times</Link></li> */}
			</ul>
		</NavbarBannerStyling>
	);
};

export default NavbarBanner;
